/**
 * 碎片组
 * 管理已吸附在一起的碎片集合
 * 组内碎片作为一个整体移动、旋转、归位
 */
import { Vector2 } from '../types';
import { PuzzlePiece } from './PuzzlePiece';
import { HexMath } from '../core/HexMath';
import { CoordinateSystem } from '../core/CoordinateSystem';

export class PieceGroup {
    private _id: string;
    private _pieces: Map<string, PuzzlePiece> = new Map();
    private _rotation: number = 0;  // 组的整体旋转角度
    private _isPlaced: boolean = false;  // 组是否已归位
    private _dirty: boolean = true; // 初始状态为 dirty

    // 组的中心位置（用于旋转计算）
    private _center: Vector2 = { x: 0, y: 0 };

    // 每个碎片相对于组中心的偏移（在组旋转为0时的偏移）
    private _pieceOffsets: Map<string, Vector2> = new Map();

    constructor(id: string) {
        this._id = id;
    }

    get id(): string {
        return this._id;
    }

    get rotation(): number {
        return this._rotation;
    }

    get isPlaced(): boolean {
        return this._isPlaced;
    }

    get center(): Vector2 {
        return { ...this._center };
    }

    get pieceCount(): number {
        return this._pieces.size;
    }

    get isDirty(): boolean {
        return this._dirty;
    }

    /**
     * 清除 dirty 标记
     */
    clearDirty(): void {
        this._dirty = false;
    }

    /**
     * 获取组内所有碎片
     */
    getPieces(): PuzzlePiece[] {
        return Array.from(this._pieces.values());
    }

    /**
     * 获取组内所有碎片ID
     */
    getPieceIds(): string[] {
        return Array.from(this._pieces.keys());
    }

    /**
     * 检查碎片是否在组内
     */
    hasPiece(pieceId: string): boolean {
        return this._pieces.has(pieceId);
    }

    /**
     * 添加碎片到组
     * @param piece 要添加的碎片
     * @param isInitial 是否是初始添加（创建组时的第一批碎片）
     */
    addPiece(piece: PuzzlePiece, isInitial: boolean = false): void {
        if (this._pieces.has(piece.id)) return;

        this._pieces.set(piece.id, piece);
        piece.setGroup(this._id);

        // 如果是初始添加，使用碎片当前的旋转角度作为组的旋转角度
        if (isInitial && this._pieces.size === 1) {
            this._rotation = piece.rotation;
        }

        // 重新计算中心和偏移
        this.recalculateCenterAndOffsets();
    }

    /**
     * 合并另一个组的所有碎片到本组
     */
    mergeGroup(otherGroup: PieceGroup): void {
        for (const piece of otherGroup.getPieces()) {
            this._pieces.set(piece.id, piece);
            piece.setGroup(this._id);
        }

        // 重新计算中心和偏移
        this.recalculateCenterAndOffsets();
    }

    /**
     * 重新计算组中心和各碎片的偏移量
     */
    private recalculateCenterAndOffsets(): void {
        if (this._pieces.size === 0) return;

        // 1. 计算当前所有碎片的平均位置作为组中心
        let sumX = 0;
        let sumY = 0;
        for (const piece of this._pieces.values()) {
            sumX += piece.position.x;
            sumY += piece.position.y;
        }
        this._center = {
            x: sumX / this._pieces.size,
            y: sumY / this._pieces.size
        };
        this._dirty = true;

        // 2. 计算所有碎片正确位置的平均值，作为偏移量的参考原点
        let sumCorrectX = 0;
        let sumCorrectY = 0;
        for (const piece of this._pieces.values()) {
            sumCorrectX += piece.correctPosition.x;
            sumCorrectY += piece.correctPosition.y;
        }
        const avgCorrectX = sumCorrectX / this._pieces.size;
        const avgCorrectY = sumCorrectY / this._pieces.size;

        // 3. 计算每个碎片相对于正确位置中心的偏移（这是 0 度时的完美偏移）
        // 使用正确位置计算偏移可以确保组内碎片永远保持完美的相对关系，消除缝隙
        this._pieceOffsets.clear();
        for (const piece of this._pieces.values()) {
            const offset = CoordinateSystem.roundVector(HexMath.sub(piece.correctPosition, { x: avgCorrectX, y: avgCorrectY }));
            this._pieceOffsets.set(piece.id, offset);
        }

        // 4. 强制对齐所有碎片到完美位置
        // 碎片位置 = 组中心 + (偏移量旋转后的值)
        for (const piece of this._pieces.values()) {
            const offset = this._pieceOffsets.get(piece.id)!;
            const worldPos = CoordinateSystem.localToWorld(offset, this._center, this._rotation);
            piece.setPosition(worldPos);
            piece.setRotation(this._rotation);
        }

        // Debug: 打印重新计算结果
        console.log(`[PieceGroup] recalculate id=${this._id}, center=(${this._center.x.toFixed(1)},${this._center.y.toFixed(1)}), rot=${this._rotation}, pieces=${this._pieces.size}`);
        for (const piece of this._pieces.values()) {
            const offset = this._pieceOffsets.get(piece.id)!;
            console.log(`[PieceGroup]   ${piece.id}(${piece.coord.u},${piece.coord.v}): pos=(${piece.position.x.toFixed(1)},${piece.position.y.toFixed(1)}), offset=(${offset.x.toFixed(1)},${offset.y.toFixed(1)}), correct=(${piece.correctPosition.x.toFixed(1)},${piece.correctPosition.y.toFixed(1)})`);
        }
    }

    /**
     * 移动整个组
     */
    move(delta: Vector2): void {
        if (this._isPlaced) return;
        if (delta.x === 0 && delta.y === 0) return;

        // 更新中心位置
        this._center.x += delta.x;
        this._center.y += delta.y;
        this._dirty = true;

        // 更新所有碎片位置
        for (const piece of this._pieces.values()) {
            piece.move(delta);
        }
    }

    /**
     * 设置组的位置（通过中心点）
     */
    setPosition(center: Vector2): void {
        if (this._isPlaced) return;

        const delta: Vector2 = {
            x: center.x - this._center.x,
            y: center.y - this._center.y
        };

        if (delta.x === 0 && delta.y === 0) return;

        this.move(delta);
        this._dirty = true;
    }

    /**
     * 旋转整个组
     * @param clockwise 是否顺时针旋转
     */
    rotate(clockwise: boolean): void {
        if (this._isPlaced) return;

        const angle = clockwise ? 60 : -60;
        this._rotation = (this._rotation + angle + 360) % 360;
        this._dirty = true;

        // 更新所有碎片的位置和旋转
        for (const piece of this._pieces.values()) {
            // 获取碎片的基础偏移（0度时的偏移）
            const baseOffset = this._pieceOffsets.get(piece.id);
            if (!baseOffset) continue;

            // 使用 CoordinateSystem 计算新世界坐标
            const worldPos = CoordinateSystem.localToWorld(baseOffset, this._center, this._rotation);
            piece.setPosition(worldPos);

            // 设置碎片旋转角度与组一致
            piece.setRotation(this._rotation);
        }
    }

    /**
     * 检查组是否可以归位
     * 条件：所有碎片都在正确位置且旋转为0
     */
    canPlace(snapDistance: number): boolean {
        // 组的旋转必须为0
        if (this._rotation !== 0) {
            return false;
        }

        // 检查每个碎片是否都在正确位置
        for (const piece of this._pieces.values()) {
            const distance = HexMath.distancePoints(piece.position, piece.correctPosition);
            if (distance > snapDistance) {
                return false;
            }
        }

        return true;
    }

    /**
     * 将组归位到正确位置
     * 前提：已通过 canPlace 检查
     */
    place(): void {
        this._isPlaced = true;
        this._rotation = 0;

        // 将所有碎片移动到正确位置并标记为已归位
        for (const piece of this._pieces.values()) {
            piece.setPosition(piece.correctPosition);
            piece.setRotation(0);
            piece.place();
        }

        // 更新中心
        this.recalculateCenterAndOffsets();
    }

    /**
     * 取消组的归位状态
     */
    unplace(): void {
        this._isPlaced = false;
        for (const piece of this._pieces.values()) {
            piece.unplace();
        }
        this._dirty = true;
    }

    /**
     * 计算组吸附到正确位置需要的偏移量
     * 返回 null 表示不能归位
     */
    getSnapToCorrectDelta(snapDistance: number): Vector2 | null {
        if (this._rotation !== 0) {
            return null;
        }

        // 使用第一个碎片计算偏移
        const firstPiece = this._pieces.values().next().value;
        if (!firstPiece) return null;

        const delta: Vector2 = {
            x: firstPiece.correctPosition.x - firstPiece.position.x,
            y: firstPiece.correctPosition.y - firstPiece.position.y
        };

        // 检查当前距离是否在吸附范围内
        const currentDistance = HexMath.vectorLength(delta);
        if (currentDistance > snapDistance) {
            return null;
        }

        // 检查应用这个偏移后，所有碎片是否都在正确位置
        for (const piece of this._pieces.values()) {
            const newPos: Vector2 = {
                x: piece.position.x + delta.x,
                y: piece.position.y + delta.y
            };
            const distance = HexMath.distancePoints(newPos, piece.correctPosition);
            if (distance > snapDistance) {
                return null;
            }
        }

        return delta;
    }

    /**
     * 获取碎片相对于组中心的偏移
     */
    getPieceOffset(pieceId: string): Vector2 | undefined {
        return this._pieceOffsets.get(pieceId);
    }

    /**
     * 导出组状态（用于存档）
     */
    exportState(): {
        id: string;
        pieceIds: string[];
        rotation: number;
        center: Vector2;
        isPlaced: boolean;
    } {
        return {
            id: this._id,
            pieceIds: this.getPieceIds(),
            rotation: this._rotation,
            center: { ...this._center },
            isPlaced: this._isPlaced
        };
    }
}
